import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ShoppingBag, Star, Gift, Check, Bell, Mail, Menu, X } from "lucide-react";
import { FreeToolsDropdown, FreeToolsMobileLinks } from "@/components/FreeToolsMenu";
import BookCover from "../components/BookCover";
import TempleQuote from "../components/TempleQuote";
import CourseBanner from "../components/CourseBanner";
import { BOOK_STATUS, AMAZON_BOOK_URL, BOOK_PRICE_LABEL, isBookLive } from "../config/bookStatus";

const IMAGES = {
  journal: "https://static.prod-images.emergentagent.com/jobs/61950cf9-271f-4d94-8d1b-3f6f6d5c3d83/images/8b09e68375ed0f05a29eb3a8b29950d077b2eee9d43239918bd466728957e58e.png",
  cards: "/card-sample-1.png",
  ritual: "https://static.prod-images.emergentagent.com/jobs/61950cf9-271f-4d94-8d1b-3f6f6d5c3d83/images/3ebec7a514163f2b0f1af374da9071618be7334b883dfaf541bddaa72cacb2cc.png",
  bundle: "https://static.prod-images.emergentagent.com/jobs/61950cf9-271f-4d94-8d1b-3f6f6d5c3d83/images/1ce52bcf184fb6f4d208043920dd216a92265585dee515a8f999b91afd204a0a.png",
};

// Stripe LIVE Payment Links
const STRIPE_LINKS = {
  journal: "https://buy.stripe.com/fZudR20bg3JR0DE2qqc7u0a",
  cards: "https://buy.stripe.com/9B6dR2e2gbcjbii4yyc7u05",
  ritual: "https://buy.stripe.com/00wcMY5vKbcjgCC6GGc7u08",
  bundleAll: "https://buy.stripe.com/7sY3co6z04NV5XY2qqc7u07",
  bundleJournalCards: "https://buy.stripe.com/cNi3co7DScgnfyyc10c7u06",
  pccWorkbook: "https://buy.stripe.com/aFa28k8HW1BJbiie98c7u0b",
  mindsetWorkbook: "https://buy.stripe.com/00w6oAbU83JRbii6GGc7u0c",
  emergencyCalmKit: "https://buy.stripe.com/fZufZaaQ494b8660iic7u0j",
  boundaryBuilder: "https://buy.stripe.com/aFacMY9M03JR4TUc10c7u0k",
};

const products = [
  {
    id: "book",
    name: '"I GET TO" — The Book (Paperback)',
    price: 19.99,
    image: "/downloads/i-get-to-book-cover.png",
    cover: {
      title: 'Stop saying "I Have To"\nStart living "I GET TO"',
      subtitle: 'Stop abandoning yourself. Redesign your life all cloaked in gratitude and brimming with opportunity.',
      variant: 'teal',
      premium: false,
    },
    description: 'Stop abandoning yourself. Redesign your life — all cloaked in gratitude and brimming with opportunity. Real-time, practical mindset shifts and the complete A.C.T. NOW Method™. By Temple Franklin & the Divine Team.',
    features: [
      "The complete 'I GET TO' philosophy",
      "The A.C.T. NOW Method™ in full",
      "Mind-Body-Spirit hygiene tips & tools",
      "Redesign your life for yourself & future generations",
      "By Temple Franklin & the Divine Team",
    ],
    tag: isBookLive() ? "Available Now" : "Coming Soon",
    stripeLink: AMAZON_BOOK_URL,
    isLive: isBookLive(),
  },
  {
    id: "pccWorkbook",
    name: 'Personal Code of Conduct Workbook',
    price: 17,
    image: "https://images.unsplash.com/photo-1672611501634-a84c6a9c45d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHx3b3JrYm9vayUyMGpvdXJuYWwlMjB0ZWFsJTIwZ29sZCUyMGVsZWdhbnR8ZW58MHx8fHwxNzc2MDQwMjQwfDA&ixlib=rb-4.1.0&q=85",
    cover: {
      title: "Personal Code of Conduct",
      subtitle: "18 acronym codes for Personal · Family · Business — living by design",
      variant: "ocean",
      premium: true,
    },
    description: "Define who you are — on YOUR terms. Not your parents' terms. Not your boss's. Not the version of you someone else needs. 18 acronym frameworks (SHIFT, BALANCE, SOAR, POWER, GROW, THANKFUL & more) for Personal, Family & Business codes. Living by design, not by default.",
    features: [
      "18 acronym-based codes of conduct (100+ principles)",
      "Personal codes: SHIFT, BALANCE, SOAR, POWER, GROW",
      "Family codes: TEAM, HEART, CARE, BRAVE, SPARK",
      "Business codes: INTEGRITY, FOCUS, GREAT, VALUE",
    ],
    tag: "Available Now",
    stripeLink: STRIPE_LINKS.pccWorkbook,
    isLive: true,
  },
  {
    id: "mindsetWorkbook",
    name: '"I GET TO" Mindset Shift Workbook',
    price: 14,
    image: "/workbook-cover.png",
    description: "Your starter toolkit for the 'I GET TO' mindset. Includes the A.C.T. NOW framework — Awareness, Clarity, Tapped In, No Opportunity Wasted — plus daily reframing exercises, 10 core affirmations, and Temple's superpower tips.",
    features: [
      "A.C.T. NOW framework: Awareness → Clarity → Tapped In → NOW",
      "Daily 'I have to → I get to' reframe practice",
      "10 core affirmations to speak daily",
      "Temple's personal superpower tips",
    ],
    tag: "Available Now",
    stripeLink: STRIPE_LINKS.mindsetWorkbook,
    isLive: true,
  },
  {
    id: "hygieneTrilogy",
    name: "The Hygiene Trilogy — Mind · Body · Spirit",
    price: 11.00,
    image: "/downloads/mini-library-gumroad-thumbnail-600x600.png",
    description: "Three soulful mini e-books — Mind Hygiene, Body Hygiene, Spirit Hygiene. Caveman-simple companions to the I GET TO book. Print them, read them slow, make them personal. Three short field guides from Temple Franklin's Mind Body Spirit HYGIENE™ Method.",
    features: [
      "3 printable PDFs — Mind, Body, Spirit Hygiene",
      "Companion reading to the I GET TO book (new material)",
      "Caveman-simple tools you can use today",
      "Instant digital download · 30-day money-back",
    ],
    tag: "Buy Now",
    stripeLink: "https://templefranklin22.gumroad.com/l/ygmyv",
    previewUrl: "/hygiene-trilogy",
    isLive: true,
  },
  {
    id: "iGetToDeck",
    name: 'The "I GET TO" Card Deck',
    price: 2.22,
    image: "/i-get-to-deck/card-00.png",
    description: "A grounded, beautifully designed daily ritual deck — 55 cards from the Mind Body Spirit HYGIENE™ Tools collection. One card, one shift, one small practice. Built to be used, not displayed.",
    features: [
      "55 unique 'I GET TO' affirmation cards",
      "Daily practice format: TODAY, I GET TO…",
      "Designed in your locked teal · cream · gold brand palette",
      "Digital download · print at home or use on phone",
    ],
    tag: "Buy Now",
    stripeLink: "https://templefranklin22.gumroad.com/l/wscmew",
    previewUrl: "/i-get-to-deck",
    isLive: true,
  },
  {
    id: "rewireDeck",
    name: 'REWIRE — Neuroplasticity Card Deck',
    price: 2.22,
    image: "/rewire-deck/cover-card.png",
    description: "Same brain. New path. A deck for catching the spiral and choosing differently. Each card names the old groove your brain is stuck in, hands you a tested new path, and gives you one line to remember when you need it most. Real tools. Real time. In your hands.",
    features: [
      "Every card walks you: OLD GROOVE → NEW PATH → REMEMBER",
      "Covers MIND · BODY · SPIRIT · HYGIENE™ categories",
      "Digital download · use anywhere in 30 seconds or less",
    ],
    tag: "Buy Now",
    stripeLink: "https://templefranklin22.gumroad.com/l/vakzb",
    previewUrl: "/rewire-deck",
    isLive: true,
  },
  {
    id: "trueNorth",
    name: "TRUE NORTH — Inner Compass Card Deck",
    price: 2.22,
    image: "/true-north/cover-card.png",
    description: "Say it. Feel it. Live it. 44 affirmation cards that build your inner compass — WORTH, COURAGE, PEACE, and TRUST. Each card walks you SAY IT → THE CALL → FEEL IT (or ASK WHY). An affirmation you can't feel is not failure — it's an invitation.",
    features: [
      "44 inner-compass cards across 4 themes",
      "Framework: SAY IT → THE CALL → FEEL IT/ASK WHY",
      "Covers SPIRIT in the Mind Body Spirit HYGIENE™ Method",
      "Printable PDF · print at home or use on phone",
    ],
    tag: "Buy Now",
    stripeLink: "https://templefranklin22.gumroad.com/l/xxykvq",
    previewUrl: "/true-north",
    isLive: true,
  },
  {
    id: "bodyKnows",
    name: "THE BODY KNOWS — Somatic Card Deck",
    price: 2.22,
    image: "/body-knows/cover-card.png",
    description: "Feel it. Ask it. Shift it. 35 somatic cards for tuning in to your body. Each card walks you NOTICE → ASK → SHIFT — for the knot in your stomach, the tight chest, the clenched jaw, the crash. Your body has been speaking your whole life. These are the tools to finally listen.",
    features: [
      "35 somatic awareness cards across 4 areas (GUT, HEART, TENSION, ENERGY)",
      "Framework: NOTICE → ASK → SHIFT",
      "Covers BODY in the Mind Body Spirit HYGIENE™ Method",
      "Printable PDF · print at home or use on phone",
    ],
    tag: "Buy Now",
    stripeLink: "https://templefranklin22.gumroad.com/l/hxhgh",
    previewUrl: "/the-body-knows",
    isLive: true,
  },
  {
    id: "kindClearDeck",
    name: "KIND & CLEAR — Boundary Card Deck",
    price: 2.22,
    image: "/kind-clear-deck/cover-card.png",
    description: "Boundaries, said with love. 44 compassionate scripts for the moments you don't know what to say. Each card names the moment, hands you the exact words, and ends the guilt spiral. You can love someone and still have no time — these are the words for both.",
    features: [
      "44 boundary scripts you'll wish you had years ago",
      "Every card walks you: THE MOMENT → SAY THIS → WHY IT'S KIND",
      "Covers MIND · BODY · SPIRIT · HYGIENE™ categories",
      "Digital download · use in person, in text, at the holiday table",
    ],
    tag: "Buy Now",
    stripeLink: "https://templefranklin22.gumroad.com/l/gvkbv",
    previewUrl: "/kind-clear-deck",
    isLive: true,
  },
  {
    id: "trilogyBundle",
    name: "The Full Method — All 5 Card Decks",
    price: 8.88,
    image: "/i-get-to-deck/card-00.png",
    description: "The complete Mind Body Spirit HYGIENE™ Method at one impulse price. Five frameworks. One Method. I GET TO (daily practice) + REWIRE (mind) + TRUE NORTH (spirit) + THE BODY KNOWS (body) + KIND & CLEAR (hygiene). Save $2.22 versus buying separately.",
    features: [
      "I GET TO Deck (55 cards · daily ritual)",
      "REWIRE Deck (neuroplasticity / MIND)",
      "TRUE NORTH Deck (44 cards · SPIRIT)",
      "THE BODY KNOWS Deck (35 cards · BODY)",
      "KIND & CLEAR Deck (44 cards · HYGIENE)",
      "Save $2.22 vs buying separately ($11.10 → $8.88)",
    ],
    tag: "Buy Now",
    stripeLink: "https://templefranklin22.gumroad.com/l/bxczrd",
    previewUrl: "/the-method",
    isLive: true,
  },
  {
    id: "emergencyCalmKit",
    name: 'Emergency Calm Kit',
    price: 11,
    image: IMAGES.ritual,
    cover: {
      title: "Emergency Calm Kit",
      subtitle: "Breathwork · Grounding · Affirmations for anxious moments",
      variant: "teal",
    },
    description: "5 breathing exercise cards, the 5-4-3-2-1 grounding technique, and 10 emergency affirmation cards. Print, laminate, carry everywhere.",
    features: [
      "5 breathing exercise cards (box, 4-7-8, belly & more)",
      "5-4-3-2-1 grounding technique worksheet",
      "10 emergency affirmation cards",
      "4-step Emergency Calm Sequence",
    ],
    tag: "Available Now",
    stripeLink: STRIPE_LINKS.emergencyCalmKit,
    previewUrl: "/emergency-calm-kit",
    isLive: true,
  },
  {
    id: "boundaryBuilder",
    name: 'The Boundary Builder — Relationship Workbook',
    price: 14,
    image: IMAGES.ritual,
    cover: {
      title: "The Boundary Builder",
      subtitle: "Relationship Workbook — 20 scripts for saying no with love",
      variant: "deep",
      premium: true,
    },
    description: "For people pleasers, empaths, and kind souls who struggle to say no. 20 real boundary scripts with exact words to say, a 'Where are my boundaries weak?' assessment, family conversation guide, and 'I GET TO say no' affirmations.",
    features: [
      "20 boundary scripts with exact words to use",
      "'Where are my boundaries weak?' self-assessment",
      "Family boundary conversation guide",
      "'I GET TO say no' affirmation practice cards",
    ],
    tag: "Available Now",
    stripeLink: STRIPE_LINKS.boundaryBuilder,
    previewUrl: "/boundary-builder",
    isLive: true,
  },
];

// NOTE: The `bundles` array previously held 2 "Coming Soon" bundle products
// (Complete Bundle + Journal+Cards Bundle). Archived to
// /app/frontend/src/_archive/coming-soon-products.js on 2026-06-10.
// Restore reminder: /app/memory/REMINDER_RESTORE_COMING_SOON_PRODUCTS.md

// Short blurbs for compact cards (≤90 chars). Keeps catalog scannable.
const SHORT_BLURBS = {
  book: "The complete I GET TO philosophy + A.C.T. NOW Method™. The core book.",
  hygieneTrilogy: "3 mini e-books — Mind, Body, Spirit Hygiene. Companions to the I GET TO book.",
  iGetToDeck: "55 daily 'I GET TO' affirmation cards. One card, one shift.",
  rewireDeck: "Same brain. New path. Neuroplasticity deck for spiral moments.",
  trueNorth: "44 affirmation cards. Worth · Courage · Peace · Trust.",
  bodyKnows: "35 somatic cards. NOTICE → ASK → SHIFT. Your body's been speaking.",
  kindClearDeck: "44 boundary scripts. Exact words to say no, with love.",
  trilogyBundle: "All 5 card decks. One impulse price. Save $2.22.",
  pccWorkbook: "18 acronym codes for Personal · Family · Business. Living by design.",
  mindsetWorkbook: "Starter toolkit. A.C.T. NOW framework + daily reframes + affirmations.",
  emergencyCalmKit: "Breathwork, grounding, emergency affirmations. Instant relief.",
  boundaryBuilder: "20 boundary scripts with exact words to say. Say no with love.",
};

const ProductCard = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-3xl overflow-hidden shadow-sm border border-[#CCFBF1] hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`relative overflow-hidden aspect-square ${(product.id === "book" || product.id === "mindsetWorkbook") ? "bg-[#efeae7] border-b-2 border-[#068fa2]/20" : "bg-[#F0F5F4]"}`}>
        {product.cover ? (
          <div className={`w-full h-full transition-transform duration-700 ${isHovered ? "scale-105" : "scale-100"}`}>
            <BookCover
              title={product.cover.title}
              subtitle={product.cover.subtitle}
              variant={product.cover.variant}
              premium={product.cover.premium}
              showKicker={product.id !== "book"}
              author2={product.cover.author2}
            />
          </div>
        ) : (
          <img
            src={product.image}
            alt={product.name}
            className={`w-full h-full transition-transform duration-700 ${(product.id === "book" || product.id === "mindsetWorkbook") ? "object-contain p-4" : "object-cover"} ${isHovered ? "scale-110" : "scale-100"}`}
          />
        )}
        {product.tag && (
          <span className={`absolute top-4 left-4 text-white text-xs font-sans font-semibold px-3 py-1.5 rounded-full ${
            product.tag === "Coming Soon" ? "bg-[#D97706]" : product.tag === "Available Now" ? "bg-[#25a1c2]" : "bg-[#25a1c2]"
          }`}>
            {product.tag}
          </span>
        )}
      </div>

      <div className="p-6 md:p-8 flex flex-col flex-1">
        <h3 className="font-serif text-xl md:text-2xl tracking-tight text-[#042F2E] mb-2">
          {product.name}
        </h3>
        <p className="font-sans text-[#475569] text-sm leading-relaxed mb-5">
          {product.description}
        </p>

        <ul className="space-y-2 mb-6 flex-1">
          {product.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#068fa2]">
              <Check className="w-4 h-4 text-[#25a1c2] mt-0.5 shrink-0" />
              <span className="font-sans">{feature}</span>
            </li>
          ))}
        </ul>

        {product.previewUrl && (
          <div className="mb-4">
            <Link
              to={product.previewUrl}
              data-testid={`preview-btn-${product.id}`}
              className="inline-flex items-center gap-1 text-[#25a1c2] hover:text-[#068fa2] font-sans text-sm font-medium transition-colors"
            >
              <ArrowRight className="w-3 h-3" />
              Preview full workbook
            </Link>
          </div>
        )}

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-[#CCFBF1] gap-3">
          <span className="font-serif text-3xl text-[#042F2E]">
            {product.price ? `$${product.price}` : "Price TBA"}
          </span>
          {product.isLive ? (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <a
                href={product.stripeLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`buy-btn-${product.id}`}
                className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-5 py-2.5 font-sans font-medium text-sm transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
              >
                <ShoppingBag className="w-4 h-4" />
                Buy Now
              </a>
              {product.gumroadLink && (
                <a
                  href={product.gumroadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`gumroad-btn-${product.id}`}
                  className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white rounded-full px-5 py-2.5 font-sans font-medium text-sm transition-all hover:-translate-y-0.5 shadow-md"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Gumroad
                </a>
              )}
            </div>
          ) : (
            <Link
              to={`/workbook?from=product-${product.id}`}
              data-testid={`notify-btn-${product.id}`}
              className="inline-flex items-center gap-2 bg-[#068fa2] hover:bg-[#042F2E] text-white rounded-full px-6 py-3 font-sans font-medium text-sm transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
            >
              <Bell className="w-4 h-4" />
              Get Notified
            </Link>
          )}
        </div>
        {product.isLive && (
          <p className="text-xs text-[#94A3B8] mt-3 text-right">
            All sales final &middot;{" "}
            <Link to="/refund-policy" className="underline hover:text-[#25a1c2]">
              why?
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

// Compact card — for use in tight grids (decks, workbooks). No long feature list.
const CompactCard = ({ product, rotate = 0 }) => {
  return (
    <div
      data-testid={`compact-card-${product.id}`}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-[#CCFBF1] hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <div className="relative overflow-hidden aspect-[5/7] bg-[#F0F5F4]">
        {product.cover ? (
          <BookCover
            title={product.cover.title}
            subtitle={product.cover.subtitle}
            variant={product.cover.variant}
            premium={product.cover.premium}
            showKicker={product.id !== "book"}
          />
        ) : (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        )}
        {product.tag === "Coming Soon" && (
          <span className="absolute top-2 left-2 bg-[#D97706] text-white text-[10px] font-sans font-semibold px-2 py-1 rounded-full uppercase tracking-wider">
            Soon
          </span>
        )}
      </div>
      <div className="p-3 md:p-4 flex flex-col flex-1">
        <h3 className="font-serif text-sm md:text-base tracking-tight text-[#042F2E] mb-1 leading-tight line-clamp-2">
          {product.name}
        </h3>
        <p className="font-sans text-[#475569] text-xs leading-snug mb-3 line-clamp-2 flex-1">
          {product.shortBlurb || product.description}
        </p>
        <div className="flex items-center justify-between gap-2 mt-auto">
          <span className="font-serif text-lg md:text-xl text-[#042F2E] font-bold">
            {product.price ? `$${product.price}` : "TBA"}
          </span>
          {product.isLive ? (
            <a
              href={product.stripeLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`compact-buy-${product.id}`}
              className="inline-flex items-center gap-1 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-3 py-1.5 font-sans font-semibold text-xs transition-all"
            >
              Buy
              <ArrowRight className="w-3 h-3" />
            </a>
          ) : (
            <Link
              to={product.previewUrl || `/workbook?from=product-${product.id}`}
              data-testid={`compact-notify-${product.id}`}
              className="inline-flex items-center gap-1 bg-[#068fa2] hover:bg-[#042F2E] text-white rounded-full px-3 py-1.5 font-sans font-semibold text-xs transition-all"
            >
              {product.tag === "Coming Soon" ? "Notify" : "View"}
              <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};


// Compact horizontal banner-style product row — image left, content right.
// Best-practice scannable layout: ~110px tall on mobile, ~140px on desktop.
// All 13 products fit in ~1500px vs the old ~4700px layout.
const HorizontalProductRow = ({ product }) => {
  const blurb = product.shortBlurb || product.description;
  return (
    <div
      data-testid={`row-${product.id}`}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#CCFBF1] hover:shadow-md hover:border-[#068fa2]/40 transition-all duration-300 flex"
    >
      {/* Left: square thumbnail — always use the real image file (no BookCover render)
          so portrait book covers and square deck cards both display cleanly */}
      <div className="relative shrink-0 w-24 sm:w-28 md:w-32 bg-[#efeae7] flex items-center justify-center overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain"
          loading="lazy"
        />
        {product.tag === "Coming Soon" && (
          <span className="absolute top-1 left-1 text-[9px] bg-[#D97706] text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide">
            Soon
          </span>
        )}
      </div>

      {/* Right: title, blurb, price + CTA */}
      <div className="flex-1 p-3 sm:p-4 flex flex-col gap-1 min-w-0">
        <h3 className="font-serif text-base md:text-lg leading-tight text-[#042F2E]">
          {product.name}
        </h3>
        <p className="font-sans text-xs md:text-sm text-[#5F7370] leading-snug line-clamp-2 flex-1">
          {blurb}
        </p>
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="font-serif text-xl md:text-2xl font-bold text-[#042F2E] whitespace-nowrap">
            {product.price ? `$${product.price}` : "TBA"}
          </span>
          <div className="flex gap-1.5 items-center flex-wrap justify-end">
            {product.previewUrl && product.isLive && (
              <Link
                to={product.previewUrl}
                data-testid={`row-preview-${product.id}`}
                className="text-xs text-[#25a1c2] hover:text-[#068fa2] font-medium underline-offset-2 hover:underline whitespace-nowrap"
              >
                Preview
              </Link>
            )}
            {product.isLive ? (
              <a
                href={product.stripeLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`row-buy-${product.id}`}
                className="inline-flex items-center gap-1 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-3 py-1.5 text-xs font-semibold transition-colors shadow-sm"
              >
                <ShoppingBag className="w-3 h-3" />
                Buy
              </a>
            ) : (
              <Link
                to={`/workbook?from=product-${product.id}`}
                data-testid={`row-notify-${product.id}`}
                className="inline-flex items-center gap-1 bg-[#068fa2] hover:bg-[#042F2E] text-white rounded-full px-3 py-1.5 text-xs font-semibold transition-colors shadow-sm"
              >
                <Bell className="w-3 h-3" />
                Notify
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Section header used between groups of horizontal rows
const CatSection = ({ id, eyebrow, title, children }) => (
  <section id={id} data-testid={`cat-${id}`} className="mb-8 scroll-mt-24">
    <div className="mb-3">
      <p className="uppercase tracking-[0.25em] text-[10px] font-bold mb-1" style={{ color: "#d4af6a" }}>
        {eyebrow}
      </p>
      <h2 className="font-serif text-xl md:text-2xl text-[#042F2E]">{title}</h2>
    </div>
    <div className="flex flex-col gap-2.5 md:gap-3">{children}</div>
  </section>
);


const BundleCard = ({ bundle }) => {
  return (
    <div
      data-testid={`bundle-card-${bundle.id}`}
      className="group bg-gradient-to-br from-[#068fa2] to-[#25a1c2] rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
    >
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-2/5 relative overflow-hidden">
          <img
            src={bundle.image}
            alt={bundle.name}
            className="w-full h-64 lg:h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          {bundle.tag && (
            <span className={`absolute top-4 left-4 text-xs font-sans font-bold px-3 py-1.5 rounded-full ${
              bundle.tag === "Coming Soon" ? "bg-[#D97706] text-white" : "bg-white text-[#25a1c2]"
            }`}>
              {bundle.tag}
            </span>
          )}
        </div>

        <div className="lg:w-3/5 p-8 md:p-10 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-5 h-5 text-[#5EEAD4]" />
            <span className="font-sans text-sm text-[#5EEAD4] font-medium tracking-wide uppercase">
              Bundle Deal
            </span>
          </div>

          <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-white mb-2">
            {bundle.name}
          </h3>
          <p className="font-sans text-[#5EEAD4] text-sm mb-4">
            {bundle.subtitle}
          </p>
          <p className="font-sans text-white/80 text-sm leading-relaxed mb-6">
            {bundle.description}
          </p>

          <div className="flex items-end gap-3 mb-6">
            <span className="font-serif text-4xl text-white">${bundle.price}</span>
            <span className="font-sans text-lg text-white/50 line-through mb-1">
              ${bundle.originalPrice}
            </span>
            <span className="font-sans text-sm bg-[#5EEAD4] text-[#068fa2] font-bold px-2.5 py-1 rounded-full mb-1">
              Save ${bundle.savings}
            </span>
          </div>

          <Link
            to={`/workbook?from=bundle-${bundle.id}`}
            data-testid={`notify-btn-${bundle.id}`}
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#F0FDF4] text-[#25a1c2] rounded-full px-8 py-4 font-sans font-semibold transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl w-full lg:w-auto"
          >
            <Bell className="w-5 h-5" />
            Coming Soon — Get Notified
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// BookHero — Featured hero section showcasing the published paperback.
// Renders at the very top of the Shop landing experience.
// CTA adapts to BOOK_STATUS flag in /config/bookStatus.js:
//   - PRE_LAUNCH → "Get Notified" button → /workbook?from=book-waitlist
//   - LIVE       → "Order paperback · $19.99" → live Lulu URL
// ============================================================================
const BookHero = () => {
  const live = isBookLive();
  return (
    <section
      data-testid="book-hero-section"
      className="relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #042F2E 0%, #068fa2 55%, #25a1c2 100%)",
      }}
    >
      {/* subtle gold radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 80% 30%, rgba(212,175,106,0.15) 0%, transparent 55%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-6 py-14 md:py-20 grid md:grid-cols-2 gap-10 md:gap-14 items-center">
        {/* Cover */}
        <div className="flex justify-center md:justify-start">
          <Link
            to="/book"
            data-testid="book-hero-cover-link"
            className="relative block transition-transform hover:-translate-y-1 hover:scale-[1.02]"
          >
            <img
              src="/book-cover-v2.png"
              alt="Stop Saying I Have To. Start Living I GET TO. — book cover by Temple Franklin"
              className="w-64 md:w-80 lg:w-96 h-auto rounded-md shadow-2xl border border-white/10"
              style={{ aspectRatio: "6 / 9" }}
            />
            {/* corner badge */}
            <span
              className="absolute -top-3 -right-3 px-3 py-1.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-sans font-bold shadow-lg"
              style={{
                backgroundColor: live ? "#5EEAD4" : "#d4af6a",
                color: "#042F2E",
              }}
              data-testid="book-hero-badge"
            >
              {live ? "Now Available" : "Just Published"}
            </span>
          </Link>
        </div>

        {/* Copy */}
        <div className="text-[#efeae7]">
          <p
            className="font-sans text-xs tracking-[0.3em] uppercase font-semibold mb-3"
            style={{ color: "#d4af6a" }}
            data-testid="book-hero-eyebrow"
          >
            ✦ The Book · Paperback · ISBN 979-8-9964172-0-9
          </p>
          <h2
            className="font-serif tracking-tighter leading-[1.02] mb-5"
            style={{ fontSize: "clamp(2.25rem, 5vw, 3.75rem)" }}
            data-testid="book-hero-title"
          >
            Stop Saying{" "}
            <span className="italic text-white/70">&ldquo;I Have To.&rdquo;</span>{" "}
            <br className="hidden md:block" />
            Start Living{" "}
            <span className="italic" style={{ color: "#5EEAD4" }}>
              &ldquo;I GET TO.&rdquo;
            </span>
          </h2>
          <p
            className="font-serif italic text-lg md:text-xl mb-5 leading-relaxed"
            style={{ color: "rgba(239,234,231,0.92)" }}
            data-testid="book-hero-tagline"
          >
            The Mind Body Spirit HYGIENE™ Method — real tools, for real women,
            in the messy middle.
          </p>
          <p
            className="font-sans text-sm md:text-base leading-relaxed mb-7 max-w-xl"
            style={{ color: "rgba(239,234,231,0.82)" }}
            data-testid="book-hero-description"
          >
            141 pages. The complete A.C.T. NOW Method™, the Personal Code of
            Conduct framework, and honest language for divorce, grief,
            estrangement, and the parts of the story nobody warned you about. A
            book to live with. A practice to come home to.
          </p>

          <div className="flex items-baseline gap-3 mb-7">
            <span
              className="font-serif"
              style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#5EEAD4" }}
              data-testid="book-hero-price"
            >
              {BOOK_PRICE_LABEL}
            </span>
            <span className="font-sans text-xs uppercase tracking-widest text-white/60">
              Paperback · 6×9
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            {live ? (
              <a
                href={AMAZON_BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (typeof window !== "undefined" && typeof window.gtag === "function") {
                    window.gtag("event", "book_buy_click", {
                      source_page: "homepage_book_hero",
                      item_name: "I GET TO Paperback",
                      value: 19.99,
                      currency: "USD",
                    });
                  }
                }}
                data-testid="book-hero-buy-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
                style={{ backgroundColor: "#5EEAD4", color: "#042F2E" }}
              >
                <ShoppingBag className="w-5 h-5" />
                Order the paperback · {BOOK_PRICE_LABEL}
                <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <Link
                to="/workbook?from=book-waitlist"
                data-testid="book-hero-notify-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
                style={{ backgroundColor: "#5EEAD4", color: "#042F2E" }}
              >
                <Bell className="w-5 h-5" />
                Notify me when it ships
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <Link
              to="/book"
              data-testid="book-hero-learn-more"
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-medium tracking-wide transition-colors border"
              style={{
                color: "#efeae7",
                borderColor: "rgba(239,234,231,0.35)",
              }}
            >
              Read the full story
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {live ? (
            <p
              className="font-sans text-xs mt-5 tracking-wide"
              style={{ color: "rgba(239,234,231,0.75)" }}
              data-testid="book-hero-live-note"
            >
              Not ready to buy? <Link to="/workbook?from=book-hero" className="underline underline-offset-4 hover:opacity-80" style={{ color: "#5EEAD4" }} data-testid="book-hero-workbook-link">Grab the free I GET TO workbook</Link> — same practice, instant download.
            </p>
          ) : (
            <p
              className="font-sans text-xs mt-5 tracking-wide"
              style={{ color: "rgba(239,234,231,0.65)" }}
              data-testid="book-hero-pre-launch-note"
            >
              Just published to Lulu — proof copy in transit. Drop your email
              and we&rsquo;ll tell you the moment paperbacks ship.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const ShopHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const navLinks = [
    { to: "/book", label: "The Book", testid: "shop-nav-book", primary: true },
    { to: "/build-your-own", label: "Build Your Own", testid: "shop-nav-build-your-own" },
    // Shop temporarily hidden until P1 legacy Stripe link migration (dead buy-links).
    { to: "/about", label: "About Temple", testid: "shop-nav-about" },
    { to: "/blog", label: "Blog", testid: "shop-nav-blog" },
    { to: "/contact", label: "Contact", testid: "shop-nav-contact" },
  ];

  return (
    <header
      data-testid="shop-header"
      className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#CCFBF1]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="shop-logo-link" onClick={closeMenu}>
          <img
            src="/downloads/MBSH-logo-TM-e4476e.png"
            alt="Mind Body Spirit HYGIENE™ Tools"
            className="h-12 w-auto"
          />
        </Link>

        {/* DESKTOP NAV — visible md+ */}
        <nav className="hidden md:flex items-center gap-3 md:gap-6">
          {navLinks.slice(0, 5).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`font-sans text-[#068fa2] hover:text-[#25a1c2] transition-colors text-sm md:text-base ${l.primary ? "font-medium" : ""}`}
              data-testid={l.testid}
            >
              {l.label}
            </Link>
          ))}
          <FreeToolsDropdown testidPrefix="shop-" />
          <Link
            to="/book"
            className="inline-flex items-center gap-2 bg-[#068fa2] hover:bg-[#042F2E] text-white rounded-full px-5 py-2.5 font-sans font-medium text-sm transition-all hover:-translate-y-0.5 shadow-md"
            data-testid="shop-nav-buy-book"
          >
            <ShoppingBag className="w-4 h-4" />
            Buy the Book
          </Link>
        </nav>

        {/* MOBILE — Buy button + hamburger menu */}
        <div className="flex md:hidden items-center gap-3">
          <Link
            to="/book"
            className="inline-flex items-center gap-1.5 bg-[#068fa2] hover:bg-[#042F2E] text-white rounded-full px-4 py-2 font-sans font-medium text-xs transition-all shadow-md"
            data-testid="shop-nav-buy-book-mobile"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            Buy Book
          </Link>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            data-testid="shop-mobile-menu-toggle"
            className="p-2 rounded-md text-[#042F2E] hover:bg-[#CCFBF1] transition-colors"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU DRAWER */}
      {menuOpen && (
        <div
          data-testid="shop-mobile-menu-drawer"
          className="md:hidden bg-white border-t border-[#CCFBF1] shadow-lg max-h-[80vh] overflow-y-auto"
        >
          <nav className="px-6 py-4 flex flex-col gap-1">
            <FreeToolsMobileLinks onNavigate={closeMenu} testidPrefix="shop-" />
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={closeMenu}
                data-testid={`shop-mobile-${l.testid}`}
                className="font-sans text-[#042F2E] hover:bg-[#CCFBF1] active:bg-[#CCFBF1] py-3 px-3 rounded-md transition-colors text-base font-medium border-b border-[#CCFBF1]/40 last:border-b-0"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default function Shop() {
  useEffect(() => {
    document.title = "Mind Body Spirit HYGIENE™ Method by Temple Franklin — The Book, Card Decks & Tools";
  }, []);

  // Split products into method decks (the 5 + bundle) and everything else.
  const METHOD_DECK_IDS = ["iGetToDeck", "rewireDeck", "trueNorth", "bodyKnows", "kindClearDeck"];
  const findById = (id) => products.find((p) => p.id === id);
  const withBlurb = (p) => p && ({ ...p, shortBlurb: SHORT_BLURBS[p.id] });

  // Compact catalog organized by category (new layout — horizontal banner rows)
  const book = withBlurb(findById("book"));
  const hygieneTrilogy = withBlurb(findById("hygieneTrilogy"));
  const methodBundle = withBlurb(findById("trilogyBundle"));
  const methodDecks = METHOD_DECK_IDS.map((id) => withBlurb(findById(id))).filter(Boolean);
  const pccWorkbook = withBlurb(findById("pccWorkbook"));
  const mindsetWorkbook = withBlurb(findById("mindsetWorkbook"));
  const boundaryBuilder = withBlurb(findById("boundaryBuilder"));
  const emergencyCalmKit = withBlurb(findById("emergencyCalmKit"));

  const jumpChips = [
    { label: "✨ Start Here", href: "#new" },
    { label: "Bundles", href: "#bundles" },
    { label: "Workbooks", href: "#workbooks" },
    { label: "Card Decks", href: "#decks" },
    { label: "Tools", href: "#tools" },
  ];

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="shop-page">
      <Helmet>
        <title>Mind Body Spirit HYGIENE™ Method by Temple Franklin — The Book, Trilogy &amp; Tools</title>
        <meta
          name="description"
          content="It's not a practice. It's hygiene. Real tools for real women in the messy middle. The 'I GET TO' paperback ($19.99), The Hygiene Trilogy ($11), all 5 card decks ($8.88 bundle), and printable workbooks — by Temple Franklin. Daily, not someday."
        />
        <link rel="canonical" href="https://mindbodyspirithygienetools.com/shop" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Mind Body Spirit HYGIENE™ Method — It's not a practice. It's hygiene." />
        <meta
          property="og:description"
          content="Real tools for real women in the messy middle. The 'I GET TO' book, Hygiene Trilogy, card decks &amp; workbooks. By Temple Franklin. Daily, not someday."
        />
        <meta property="og:url" content="https://mindbodyspirithygienetools.com/shop" />
        <meta property="og:image" content="https://mindbodyspirithygienetools.com/share-card.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mind Body Spirit HYGIENE™ — It's not a practice. It's hygiene." />
        <meta name="twitter:description" content="Real tools for real women in the messy middle. By Temple Franklin." />
        <meta name="twitter:image" content="https://mindbodyspirithygienetools.com/share-card.png" />
      </Helmet>
      <ShopHeader />

      {/* ─── HERO — WORKBOOK front door (Section 3 copy from brief) ─── */}
      <section
        className="pt-28 pb-10 md:pt-32 md:pb-14"
        style={{ background: "#efeae7" }}
        data-testid="shop-hero"
      >
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-[1fr,260px] gap-8 md:gap-12 items-center">
          {/* LEFT — copy + CTA */}
          <div className="text-center md:text-left">
            <p className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-3" style={{ color: "#d4af6a" }} data-testid="workbook-eyebrow-shop">
              ✦ Free printable workbook
            </p>
            <h1
              className="font-serif tracking-tighter leading-[1.05] mb-4"
              style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)", color: "#042F2E" }}
              data-testid="workbook-headline-shop"
            >
              Your brain is not stuck, Lovely&nbsp;— and two small words can prove it.
            </h1>
            <p className="font-serif text-base md:text-lg leading-relaxed mb-6" style={{ color: "#068fa2" }} data-testid="workbook-subhead-shop">
              Download <em>I GET TO</em> — a 12-page Mind Body Spirit HYGIENE™ practice. Learn the simple tool that helps you rewire how you talk to yourself — one true word at a time.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start items-center">
              <Link
                to="/workbook"
                data-testid="shop-workbook-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
                style={{ background: "#068fa2", color: "white" }}
              >
                <Mail className="w-5 h-5" />
                Send me the free workbook
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/free"
                data-testid="shop-free-resources-link"
                className="inline-flex items-center justify-center gap-1 font-sans font-medium tracking-wide text-sm transition-colors"
                style={{ color: "#068fa2" }}
              >
                or browse all free resources →
              </Link>
            </div>
            <p className="font-sans text-xs leading-relaxed mt-4 text-center md:text-left" style={{ color: "#5F7370" }}>
              Instant download + bonus 365-day affirmation calendar + a year of weekly notes. Unsubscribe anytime.
            </p>
          </div>
          {/* RIGHT — mini cover */}
          <div className="hidden md:block">
            <Link to="/workbook" data-testid="shop-workbook-cover-link" className="block transition-transform hover:-translate-y-1 hover:scale-[1.03]">
              <div
                className="aspect-[6/8] rounded-md shadow-2xl flex flex-col items-center justify-center text-center px-5 relative overflow-hidden"
                style={{
                  background: "linear-gradient(155deg, #042F2E 0%, #068fa2 60%, #25a1c2 100%)",
                }}
              >
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 70% 30%, rgba(212,175,106,0.22) 0%, transparent 55%)" }} />
                <p className="relative font-sans text-[8px] tracking-[0.4em] uppercase font-bold mb-2" style={{ color: "#d4af6a" }}>
                  12 pages · free
                </p>
                <h2 className="relative font-serif text-white tracking-tighter leading-none mb-2 text-4xl">
                  I GET TO
                </h2>
                <p className="relative font-serif italic text-xs leading-snug" style={{ color: "rgba(255,255,255,0.85)" }}>
                  A practice for rewiring your brain — one true word at a time.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════ COMPACT CATALOG ═══════════
          All products in horizontal banner-rows, grouped by category.
          Mobile-first scannable layout. Shrinks shop from ~4700px to ~1500px. */}
      <section
        className="px-4 md:px-6 pb-16 md:pb-20"
        id="catalog"
        data-testid="shop-catalog"
        style={{ backgroundColor: "#efeae7" }}
      >
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <p className="uppercase tracking-[0.3em] text-[10px] font-bold mb-2" style={{ color: "#d4af6a" }}>
              ✦ The Shop ✦
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#042F2E] mb-2">
              The full catalog
            </h2>
            <p className="font-sans text-sm md:text-base text-[#5F7370] max-w-md mx-auto">
              Tap any banner. Every tool is built for real life — not the highlight reel.
            </p>
          </div>

          {/* Category jump chips (sticky-feeling quick nav) */}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:justify-center" data-testid="shop-jump-chips">
            {jumpChips.map((c) => (
              <a
                key={c.label}
                href={c.href}
                data-testid={`jump-${c.href.replace("#", "")}`}
                className="shrink-0 px-4 py-1.5 rounded-full bg-white border border-[#068fa2]/30 text-[#068fa2] text-sm font-medium hover:bg-[#068fa2] hover:text-white transition-colors whitespace-nowrap"
              >
                {c.label}
              </a>
            ))}
          </div>

          {/* SECTION: New & Featured — Book + Trilogy (companions). */}
          <CatSection id="new" eyebrow="✦ Start here ✦" title="New &amp; Featured">
            {book && <HorizontalProductRow product={book} />}
            {hygieneTrilogy && <HorizontalProductRow product={hygieneTrilogy} />}
          </CatSection>

          {/* SECTION: Bundles — best value */}
          {methodBundle && (
            <CatSection id="bundles" eyebrow="✦ Best value ✦" title="Bundles">
              <HorizontalProductRow product={methodBundle} />
            </CatSection>
          )}

          {/* SECTION: Workbooks — the foundation work */}
          <CatSection id="workbooks" eyebrow="✦ Sit-down practice ✦" title="Workbooks">
            {pccWorkbook && <HorizontalProductRow product={pccWorkbook} />}
            {mindsetWorkbook && <HorizontalProductRow product={mindsetWorkbook} />}
            {boundaryBuilder && <HorizontalProductRow product={boundaryBuilder} />}
          </CatSection>

          {/* SECTION: Card Decks */}
          <CatSection id="decks" eyebrow="✦ One card, one shift ✦" title="Card Decks · $2.22 each">
            {methodDecks.map((deck) => (
              <HorizontalProductRow key={deck.id} product={deck} />
            ))}
          </CatSection>

          {/* SECTION: Tools & Kits */}
          {emergencyCalmKit && (
            <CatSection id="tools" eyebrow="✦ Instant relief ✦" title="Tools &amp; Kits">
              <HorizontalProductRow product={emergencyCalmKit} />
            </CatSection>
          )}

        </div>
      </section>

      {/* ─── TEMPLE QUOTE (kept — soulful brand touch) ────── */}
      <TempleQuote
        quote="For every woman who picks up this work, an entire family system shifts."
        variant="inline"
        theme="cream"
        id="quote-family-shifts-shop"
      />


      {/* ─── Free Workbook CTA ──────────────────────────── */}
      <section className="py-16 md:py-20 bg-white" data-testid="shop-email-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#25a1c2] font-semibold mb-3">
            Not ready to buy?
          </p>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#042F2E] mb-4">
            Start with the free workbook
          </h2>
          <p className="font-sans text-base text-[#068fa2] mb-7">
            A 12-page Mind Body Spirit HYGIENE™ practice — instant download. Plus a bonus 365-day calendar and a year of weekly notes from Temple.
          </p>
          <Link
            to="/workbook"
            data-testid="shop-free-cta-btn"
            className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-7 py-3.5 font-sans font-medium transition-all hover:-translate-y-0.5 shadow-md"
          >
            Send me the free workbook
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}

      {/* ════════ FLOATING STICKY BUY THE BOOK CTA (per Temple's spec) ════════ */}
      <Link
        to="/book"
        className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-40 inline-flex items-center gap-2 bg-[#068fa2] hover:bg-[#042F2E] text-white rounded-full px-5 py-3.5 font-sans font-semibold text-sm shadow-2xl transition-all hover:-translate-y-0.5"
        style={{ boxShadow: "0 12px 32px rgba(6,143,162,0.35)" }}
        data-testid="floating-buy-book"
      >
        <ShoppingBag className="w-4 h-4" />
        Buy the Book
      </Link>
    </div>
  );
}
